import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import { ExportDataTable } from '@app/modules/Home/Data/components/DataLayerTableExport'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/tailwind.utils'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown, DatabaseIcon, Eye, Search } from 'lucide-react'
import * as React from 'react'
import { useHazardTableRows } from '../hooks/useHazardTableRows'
import { HazardTableRow } from '../types/hazard.types'
import { getHiddenColumns, getStaticColumns, getSuitabilityColumns } from './HazardTable.columns'

const STATIC_COL_IDS = new Set([
  'name',
  'group',
  'confidenceRating',
  'createdAt',
  'owner',
  'publicInfo',
  'limitationsCaveats',
  'license',
  'spatialExtent',
  'provider',
])

type ColumnMetaWithHidden = {
  hidden?: boolean
}

const isHiddenColumn = (meta: unknown): boolean => {
  return Boolean((meta as ColumnMetaWithHidden | undefined)?.hidden)
}

const HazardTable = () => {
  const { rows, useCases } = useHazardTableRows()
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'name', desc: false }])
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [categoryFilterValue, setCategoryFilterValue] = React.useState('')
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
    license: false,
    spatialExtent: false,
  })

  const headerRef = React.useRef<HTMLDivElement>(null)
  const bodyRef = React.useRef<HTMLDivElement>(null)
  const footerRef = React.useRef<HTMLDivElement>(null)
  const isSyncingRef = React.useRef(false)

  // Sync horizontal scroll between header, body, and footer
  React.useEffect(() => {
    const header = headerRef.current
    const body = bodyRef.current
    const footer = footerRef.current

    if (!header || !body || !footer) return

    const onHeaderScroll = () => {
      if (isSyncingRef.current) return
      isSyncingRef.current = true
      body.scrollLeft = header.scrollLeft
      footer.scrollLeft = header.scrollLeft
      requestAnimationFrame(() => {
        isSyncingRef.current = false
      })
    }

    const onBodyScroll = () => {
      if (isSyncingRef.current) return
      isSyncingRef.current = true
      header.scrollLeft = body.scrollLeft
      footer.scrollLeft = body.scrollLeft
      requestAnimationFrame(() => {
        isSyncingRef.current = false
      })
    }

    const onFooterScroll = () => {
      if (isSyncingRef.current) return
      isSyncingRef.current = true
      header.scrollLeft = footer.scrollLeft
      body.scrollLeft = footer.scrollLeft
      requestAnimationFrame(() => {
        isSyncingRef.current = false
      })
    }

    header.addEventListener('scroll', onHeaderScroll, { passive: true })
    body.addEventListener('scroll', onBodyScroll, { passive: true })
    footer.addEventListener('scroll', onFooterScroll, { passive: true })

    return () => {
      header.removeEventListener('scroll', onHeaderScroll)
      body.removeEventListener('scroll', onBodyScroll)
      footer.removeEventListener('scroll', onFooterScroll)
    }
  }, [])

  const categories = React.useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.category)))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [rows],
  )

  const useCasesKey = useCases.map((u) => u.slug).join(',')

  const columns = React.useMemo<ColumnDef<HazardTableRow>[]>(
    () => [...getStaticColumns(), ...getHiddenColumns(), ...getSuitabilityColumns(useCases)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useCasesKey],
  )

  const columnFilters = React.useMemo<ColumnFiltersState>(
    () => (categoryFilterValue ? [{ id: 'category', value: categoryFilterValue }] : []),
    [categoryFilterValue],
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter, columnFilters, columnVisibility },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: () => {
      // intentional no-op — columnFilters controlled externally via memo
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
    enableSortingRemoval: false,
  })

  const visibleRows = table.getRowModel().rows
  const visibleColumnCount = table.getVisibleLeafColumns().length - 1

  const exportColumns = React.useMemo(
    () =>
      table
        .getVisibleLeafColumns()
        .filter((col) => col.id !== 'category')
        .map((col) => ({
          id: col.id,
          header: typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useCasesKey],
  )
  const exportRows = React.useMemo(() => {
    return table.getPrePaginationRowModel().rows.map((row) => {
      return row.getAllCells().reduce<Record<string, unknown>>((acc, cell) => {
        const { id } = cell.column
        if (id.startsWith('suitability_')) {
          const slug = id.replace('suitability_', '')
          const record = row.original.suitability.find((s) => s.use_case_slug === slug) ?? null
          acc[id] = record?.suitability_grade ?? '—'
        } else {
          acc[id] = cell.getValue()
        }
        return acc
      }, {})
    })
  }, [table])

  return (
    <div className="flex flex-col gap-4 rounded-lg border bg-card p-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search hazards"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8 w-64"
            />
          </div>

          {/* Category Filter */}
          <Select
            value={categoryFilterValue || undefined}
            onValueChange={(value) => setCategoryFilterValue(value)}
          >
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Select hazard type" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Reset Filter Button */}
          {(categoryFilterValue || globalFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCategoryFilterValue('')
                setGlobalFilter('')
              }}
            >
              Reset filter
            </Button>
          )}
        </div>
        <ExportDataTable
          size="small"
          columns={exportColumns}
          rows={exportRows}
          fileName="risk-data-hazards.csv"
        />
        {/* Column Visibility Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Eye className="h-4 w-4" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Hazard Information
            </DropdownMenuLabel>
            {table
              .getAllLeafColumns()
              .filter(
                (c) =>
                  c.getCanHide() && !isHiddenColumn(c.columnDef.meta) && STATIC_COL_IDS.has(c.id),
              )
              .map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                >
                  {typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id}
                </DropdownMenuCheckboxItem>
              ))}
            {table
              .getAllLeafColumns()
              .some((c) => c.id.startsWith('suitability_') && c.getCanHide()) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Use Cases
                </DropdownMenuLabel>
                {table
                  .getAllLeafColumns()
                  .filter((c) => c.id.startsWith('suitability_') && c.getCanHide())
                  .map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id}
                    </DropdownMenuCheckboxItem>
                  ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Table */}
      <div className="flex flex-col rounded-md border overflow-hidden">
        {/* Fixed Header */}
        <div
          ref={headerRef}
          className="overflow-x-auto overflow-y-hidden border-b bg-background [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          <table className="w-full caption-bottom text-sm table-fixed">
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers
                    .filter((header) => !isHiddenColumn(header.column.columnDef.meta))
                    .map((header) => {
                      const isName = header.column.id === 'name'
                      const isUseCase = header.column.id.startsWith('suitability_')
                      const canSort = header.column.getCanSort()
                      const sortDir = header.column.getIsSorted()

                      return (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'font-medium',
                            isUseCase && 'text-center',
                            isName && 'sticky left-0 bg-background z-20 border-r border-border',
                          )}
                          style={{ width: header.getSize(), minWidth: header.getSize() }}
                        >
                          {canSort ? (
                            <button
                              className="flex items-center gap-1 hover:text-foreground w-full"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <span className="truncate">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                              {sortDir === 'asc' && <ArrowUp className="h-4 w-4 flex-shrink-0" />}
                              {sortDir === 'desc' && (
                                <ArrowDown className="h-4 w-4 flex-shrink-0" />
                              )}
                              {sortDir === false && (
                                <ArrowUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                              )}
                            </button>
                          ) : (
                            <div className="truncate">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </div>
                          )}
                        </TableHead>
                      )
                    })}
                </TableRow>
              ))}
            </TableHeader>
          </table>
        </div>

        {/* Scrollable Body */}
        <div ref={bodyRef} className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-380px)]">
          <table className="w-full caption-bottom text-sm table-fixed">
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-64 text-center">
                    <EmptyData title="No results found" icon={<DatabaseIcon />} />
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row) => (
                  <TableRow key={row.id}>
                    {row
                      .getVisibleCells()
                      .filter((cell) => !isHiddenColumn(cell.column.columnDef.meta))
                      .map((cell) => {
                        const isName = cell.column.id === 'name'
                        const isUseCase = cell.column.id.startsWith('suitability_')

                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              isUseCase && 'text-center',
                              isName && 'sticky left-0 bg-background z-10 border-r border-border',
                            )}
                            style={{
                              width: cell.column.getSize(),
                              minWidth: cell.column.getSize(),
                            }}
                          >
                            <div className="truncate">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          </TableCell>
                        )
                      })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </table>
        </div>

        {/* Fixed Footer */}
        <div ref={footerRef} className="overflow-x-auto border-t bg-background">
          <table className="w-full caption-bottom text-sm table-fixed">
            <TableFooter>
              <TableRow>
                <TableCell colSpan={visibleColumnCount} className="sticky left-0">
                  Showing {visibleRows.length} of {rows.length} layer{rows.length !== 1 ? 's' : ''}
                </TableCell>
              </TableRow>
            </TableFooter>
          </table>
        </div>
      </div>
    </div>
  )
}

export default HazardTable
